import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/auth.service';

export const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const token = localStorage.getItem('vendor_token');
            const registrationId = localStorage.getItem('current_registration_id');
            
            if (token) {
                // If we have registration data, this might be a temporary token
                if (registrationId) {
                    // Don't try to get user data for temporary registration tokens
                    // Just set loading to false and let the route protection handle it
                    setLoading(false);
                    return;
                }
                
                // Only try to get user data for existing vendors
                const userData = await authService.getCurrentUser();
                setUser(userData);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            // Only remove token if we don't have registration data
            const registrationId = localStorage.getItem('current_registration_id');
            if (!registrationId) {
                localStorage.removeItem('vendor_token');
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (mobile, otp) => {
        console.log('AuthContext: Starting login process');
        const response = await authService.login(mobile, otp);
        console.log('AuthContext: Login response:', response);
        
        // Check if user data is in the response
        if (response.user) {
            setUser(response.user);
            console.log('AuthContext: User set from response:', response.user);
            
            // Clear old registration data since this is an existing vendor login
            localStorage.removeItem('current_registration_id');
            localStorage.removeItem('vendor_mobile_number');
            console.log('AuthContext: Cleared old registration data');
        } else if (response.access_token) {
            // If no user data but we have a token, try to get user data
            console.log('AuthContext: No user data in response, attempting to get current user');
            try {
                const userData = await authService.getCurrentUser();
                console.log('AuthContext: Retrieved user data:', userData);
                setUser(userData);
                
                // Clear old registration data since this is an existing vendor login
                localStorage.removeItem('current_registration_id');
                localStorage.removeItem('vendor_mobile_number');
                console.log('AuthContext: Cleared old registration data');
            } catch (error) {
                console.error('AuthContext: Failed to get current user:', error);
                // Set a basic user object with the mobile number
                setUser({
                    id: 'temp_' + Date.now(),
                    mobile_number: mobile,
                    is_temp: true
                });
            }
        }
        
        localStorage.setItem('vendor_token', response.access_token);
        return response;
    };

    const register = async (userData) => {
        const response = await authService.register(userData);
        return response;
    };

    const requestOTP = async (mobile) => {
        const response = await authService.requestOTP(mobile);
        return response;
    };

    const logout = () => {
        localStorage.removeItem('vendor_token');
        localStorage.removeItem('current_registration_id');
        localStorage.removeItem('vendor_mobile_number');
        setUser(null);
        setNotification(null);
    };

    const clearNotification = () => {
        setNotification(null);
    };

    // Check if user is a registering vendor (has temporary token)
    const isRegisteringVendor = () => {
        const registrationId = localStorage.getItem('current_registration_id');
        const mobileNumber = localStorage.getItem('vendor_mobile_number');
        const token = localStorage.getItem('vendor_token');
        
        // If we have registration data but no proper user, we're registering
        if (registrationId && mobileNumber) {
            // If we have a token but no user data yet, we might be registering
            if (token && !user) {
                console.log('Auth: Registering vendor (has token but no user data)');
                return true;
            }
            // If we have no token at all, we're definitely registering
            if (!token) {
                console.log('Auth: Registering vendor (no token)');
                return true;
            }
        }
        console.log('Auth: Not registering vendor');
        return false;
    };

    // Check if user is an existing vendor (has proper authentication)
    const isExistingVendor = () => {
        console.log('Auth: Checking if existing vendor - user:', user, 'token:', !!localStorage.getItem('vendor_token'));
        
        // Must have both user data and token
        if (!user || !localStorage.getItem('vendor_token')) {
            console.log('Auth: Not existing vendor (no user or token)');
            return false;
        }
        
        // If we have registration data, we're not an existing vendor yet
        const registrationId = localStorage.getItem('current_registration_id');
        if (registrationId) {
            console.log('Auth: Not existing vendor (has registration data)');
            return false;
        }
        
        // User must have an ID to be considered an existing vendor
        const result = !!(user.id);
        console.log('Auth: Existing vendor check result:', result, 'user.id:', user.id);
        return result;
    };

    // Check if user is authenticated (either registering or existing)
    const isAuthenticated = () => {
        return isExistingVendor() || isRegisteringVendor();
    };

    const value = {
        user,
        loading,
        notification,
        login,
        register,
        requestOTP,
        logout,
        clearNotification,
        checkAuth,
        isRegisteringVendor,
        isExistingVendor,
        isAuthenticated
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 